import {
  Box,
  Button,
  Text,
  HStack,
  Icon,
  IconButton,
  VStack,
  Fade,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RiCloseLine,
  RiNotificationFill,
  RiNotificationLine,
} from 'react-icons/ri';
import { useHistory } from 'react-router-dom';
import { INotificationBase } from '../../models/notifications';
import { listNotifications } from '../../services/Notifications/listNotifications';
import { markAllNotificationsAsRead } from '../../services/Notifications/markAllNotificationsAsRead';
import { updateNotifications } from '../../services/Notifications/updateNotifications';
import { socket } from '../../services/Socket';

interface IRemoveNotificationsProps {
  bookingId?: string;
  bookingQueueId?: string;
}

const notificationsPerPage = 10;

export const NotificationsNav = (): JSX.Element => {
  const { push } = useHistory();
  const [notifications, setNotifications] = useState<INotificationBase[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showReadNotifications, setShowReadNotifications] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);

  const hasViewPendingNotifications = useMemo(
    () => unreadTotal > 0,
    [unreadTotal],
  );

  const loadNotifications = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await listNotifications({
        isUnread: !showReadNotifications,
        limit: notificationsPerPage,
        page: currentPage,
      });

      setNotifications(response.items);
      setTotalPages(response.pages || 1);

      if (!showReadNotifications) {
        setUnreadTotal(response.total);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, showReadNotifications]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    function addNotifications(notification: INotificationBase): void {
      if (notification.isUnread) {
        setUnreadTotal((prevState) => prevState + 1);
      }

      if (
        !showReadNotifications &&
        notification.isUnread &&
        currentPage === 1
      ) {
        setNotifications((prevState) =>
          [notification, ...prevState].slice(0, notificationsPerPage),
        );
      }
    }

    function removeNotifications({
      bookingId,
      bookingQueueId,
    }: IRemoveNotificationsProps): void {
      if (bookingId) {
        setNotifications((prevState) =>
          prevState.filter(
            (notification) => notification.bookingId !== bookingId,
          ),
        );
      }

      if (bookingQueueId) {
        setNotifications((prevState) =>
          prevState.filter(
            (notification) => notification.bookingQueueId !== bookingQueueId,
          ),
        );
      }
    }

    socket.on('booking-notification', addNotifications);
    socket.on('cancel-booking-notification', removeNotifications);

    return () => {
      socket.off('booking-notification', addNotifications);
      socket.off('cancel-booking-notification', removeNotifications);
    };
  }, [currentPage, showReadNotifications]);

  const toggleShowNotifications = (): void => {
    setShowNotifications((prevState) => !prevState);
  };

  const setNotificationRead = async (notificationId: string): Promise<void> => {
    setNotifications((prevState) =>
      prevState.filter((notification) => notification.id !== notificationId),
    );
    setUnreadTotal((prevState) => Math.max(prevState - 1, 0));

    await updateNotifications({
      isUnread: false,
      notificationId,
    });
  };

  const setAllNotificationsRead = async (): Promise<void> => {
    setIsMarkingAllAsRead(true);

    try {
      await markAllNotificationsAsRead();

      setUnreadTotal(0);
      setCurrentPage(1);

      if (!showReadNotifications) {
        setNotifications([]);
        setTotalPages(1);
      } else {
        const response = await listNotifications({
          isUnread: false,
          limit: notificationsPerPage,
          page: 1,
        });

        setNotifications(response.items);
        setTotalPages(response.pages || 1);
      }
    } catch {
      await loadNotifications();
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  const showBookingDetails = async (
    notification: INotificationBase,
  ): Promise<void> => {
    if (notification.isUnread) {
      await setNotificationRead(notification.id);
    }

    if (notification.bookingId) {
      push('/bookings/details', {
        bookingId: notification.bookingId,
      });
    }

    if (notification.bookingQueueId) {
      push('/booking-queues/details', {
        bookingQueueId: notification.bookingQueueId,
      });
    }
  };

  const toggleReadNotifications = (showRead: boolean): void => {
    setShowReadNotifications(showRead);
    setCurrentPage(1);
  };

  const goToPreviousPage = (): void => {
    setCurrentPage((prevState) => Math.max(prevState - 1, 1));
  };

  const goToNextPage = (): void => {
    setCurrentPage((prevState) => Math.min(prevState + 1, totalPages));
  };

  return (
    <Box position="relative">
      <HStack
        spacing={['6', '8']}
        mx={['6', '8']}
        pr={['6', '8']}
        py="1"
        borderRightWidth={1}
        borderColor="gray.700"
      >
        <Button
          onClick={toggleShowNotifications}
          _focus={{ borderColor: 'transparent' }}
        >
          {!hasViewPendingNotifications ? (
            <Icon as={RiNotificationLine} fontSize="24" />
          ) : (
            <Icon as={RiNotificationFill} fontSize="24" fill="red.500" />
          )}
        </Button>
      </HStack>

      <Box
        display={showNotifications ? 'block' : 'none'}
        zIndex={10000}
        position="absolute"
        top={16}
      >
        <Fade in={showNotifications}>
          <VStack
            bg="black"
            borderRadius={8}
            opacity={0.75}
            w="22rem"
            h="96"
            p="2"
          >
            <HStack justifyContent="space-between" w="full" pl="2">
              <Text color="white">Notificações</Text>

              <IconButton
                colorScheme="blackAlpha"
                aria-label="close-notifications"
                onClick={toggleShowNotifications}
                _focus={{ borderColor: 'transparent' }}
              >
                <Icon as={RiCloseLine} fontSize="24" />
              </IconButton>
            </HStack>

            <HStack w="full" px="2">
              <Button
                size="xs"
                flex={1}
                colorScheme={!showReadNotifications ? 'blue' : 'blackAlpha'}
                onClick={() => toggleReadNotifications(false)}
                _focus={{ borderColor: 'transparent' }}
              >
                Não lidas
              </Button>
              <Button
                size="xs"
                flex={1}
                colorScheme={showReadNotifications ? 'blue' : 'blackAlpha'}
                onClick={() => toggleReadNotifications(true)}
                _focus={{ borderColor: 'transparent' }}
              >
                Lidas
              </Button>
            </HStack>

            <Button
              size="xs"
              w="calc(100% - 1rem)"
              colorScheme="blackAlpha"
              disabled={unreadTotal <= 0 || isLoading || isMarkingAllAsRead}
              isLoading={isMarkingAllAsRead}
              onClick={setAllNotificationsRead}
              _focus={{ borderColor: 'transparent' }}
            >
              Marcar todas como lidas
            </Button>

            <Box w="full" flex={1} overflowY="auto">
              {isLoading ? (
                <Text color="gray.300" fontSize="sm" p="4" textAlign="center">
                  Carregando...
                </Text>
              ) : (
                notifications.map((notification) => (
                  <VStack
                    key={notification.id}
                    align="flex-start"
                    spacing={1}
                    color="gray.300"
                    _notFirst={{
                      borderTopWidth: 1,
                      borderTopColor: 'gray.600',
                    }}
                    py="4"
                  >
                    <Button
                      w="full"
                      height="auto"
                      flex={1}
                      colorScheme="blackAlpha"
                      onClick={() => showBookingDetails(notification)}
                      disabled={
                        !notification.bookingId && !notification.bookingQueueId
                      }
                      _focus={{ borderColor: 'transparent' }}
                    >
                      <VStack
                        w="full"
                        fontWeight="normal"
                        align="flex-start"
                        spacing={2}
                        _hover={{ textDecoration: 'underline' }}
                      >
                        <Text fontSize="sm">{notification.title}</Text>
                        <Text
                          fontSize="sm"
                          whiteSpace="normal"
                          textAlign="left"
                        >
                          {notification.description}
                        </Text>
                      </VStack>
                    </Button>

                    {notification.isUnread ? (
                      <Button
                        colorScheme="blackAlpha"
                        alignSelf="center"
                        py="1"
                        px="4"
                        h="min"
                        _focus={{ borderColor: 'transparent' }}
                        onClick={() => setNotificationRead(notification.id)}
                        _hover={{ backgroundColor: 'gray.900' }}
                      >
                        <Text
                          fontWeight="normal"
                          fontSize="sm"
                          color="gray.400"
                        >
                          Marcar como visualizado
                        </Text>
                      </Button>
                    ) : (
                      <Text alignSelf="center" fontSize="sm" color="gray.400">
                        Visualizado
                      </Text>
                    )}
                  </VStack>
                ))
              )}

              {!isLoading && !notifications.length && (
                <Text color="gray.300" fontSize="sm" p="4" textAlign="center">
                  Nenhuma notificação encontrada.
                </Text>
              )}
            </Box>

            {totalPages > 1 && (
              <HStack w="full" justifyContent="space-between" px="2">
                <Button
                  size="xs"
                  colorScheme="blackAlpha"
                  disabled={currentPage <= 1 || isLoading}
                  onClick={goToPreviousPage}
                  _focus={{ borderColor: 'transparent' }}
                >
                  Anterior
                </Button>
                <Text color="gray.300" fontSize="xs">
                  {currentPage}
                  {' / '}
                  {totalPages}
                </Text>
                <Button
                  size="xs"
                  colorScheme="blackAlpha"
                  disabled={currentPage >= totalPages || isLoading}
                  onClick={goToNextPage}
                  _focus={{ borderColor: 'transparent' }}
                >
                  Próxima
                </Button>
              </HStack>
            )}
          </VStack>
        </Fade>
      </Box>
    </Box>
  );
};
